import axios from 'axios';

// Use relative URL in production (same domain), full URL only in development
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to format API errors
export function formatApiError(error) {
  if (!error.response) {
    // Network error - no response received
    return "Unable to connect to server. Please check your internet connection.";
  }
  const detail = error.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth API
export const authApi = {
  login: (email, password) => 
    axios.post(`${API_URL}/api/auth/login`, { email, password }),
  
  register: (email, password, name) => 
    axios.post(`${API_URL}/api/auth/register`, { email, password, name }),
  
  logout: () => 
    axios.post(`${API_URL}/api/auth/logout`),
  
  me: () => 
    axios.get(`${API_URL}/api/auth/me`),
  
  refresh: () => 
    axios.post(`${API_URL}/api/auth/refresh`),
  
  forgotPassword: (email) => 
    axios.post(`${API_URL}/api/auth/forgot-password`, { email }),
  
  resetPassword: (token, new_password) => 
    axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password }),
  
  validateResetToken: (token) =>
    axios.get(`${API_URL}/api/auth/validate-reset-token`, { params: { token } })
};

// User API
export const userApi = {
  updateProfile: (data) => 
    axios.put(`${API_URL}/api/user/profile`, data),
  
  updatePlan: (plan) => 
    axios.put(`${API_URL}/api/user/plan`, { plan }),
  
  getStats: () => 
    axios.get(`${API_URL}/api/user/stats`)
};

// Listings API
export const listingsApi = {
  getAll: (params) => 
    axios.get(`${API_URL}/api/listings`, { params }),
  
  getLatest: (limit = 8) => 
    axios.get(`${API_URL}/api/listings/latest`, { params: { limit } }),
  
  getMy: () => 
    axios.get(`${API_URL}/api/listings/my`),
  
  getOne: (id) => 
    axios.get(`${API_URL}/api/listings/${id}`),
  
  create: (data) => 
    axios.post(`${API_URL}/api/listings`, data),
  
  update: (id, data) => 
    axios.put(`${API_URL}/api/listings/${id}`, data),
  
  delete: (id) => 
    axios.delete(`${API_URL}/api/listings/${id}`),

  toggleSold: (id) =>
    axios.put(`${API_URL}/api/listings/${id}/sold`),

  aiGenerate: (data) =>
    axios.post(`${API_URL}/api/ai/generate-listing`, data)
};

// Jobs Community API
export const jobsApi = {
  getPosts: (params) =>
    axios.get(`${API_URL}/api/jobs/posts`, { params }),

  getPost: (id) =>
    axios.get(`${API_URL}/api/jobs/posts/${id}`),

  createPost: (data) =>
    axios.post(`${API_URL}/api/jobs/posts`, data),

  deletePost: (id) =>
    axios.delete(`${API_URL}/api/jobs/posts/${id}`),

  toggleLike: (id) =>
    axios.post(`${API_URL}/api/jobs/posts/${id}/like`),

  getComments: (postId) =>
    axios.get(`${API_URL}/api/jobs/posts/${postId}/comments`),

  createComment: (postId, data) =>
    axios.post(`${API_URL}/api/jobs/posts/${postId}/comments`, data),

  toggleCommentLike: (commentId) =>
    axios.post(`${API_URL}/api/jobs/comments/${commentId}/like`)
};

// Upload API
export const uploadApi = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/api/upload/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Categories API
export const categoriesApi = {
  getAll: () => 
    axios.get(`${API_URL}/api/categories`)
};

// Cities API
export const citiesApi = {
  getAll: () =>
    axios.get(`${API_URL}/api/cities`)
};

// Plans API
export const plansApi = {
  getAll: () => 
    axios.get(`${API_URL}/api/plans`)
};

// Chat/Conversations API
export const chatApi = {
  getConversations: () =>
    axios.get(`${API_URL}/api/conversations`),
  
  getConversation: (id) =>
    axios.get(`${API_URL}/api/conversations/${id}`),
  
  createConversation: (listing_id) =>
    axios.post(`${API_URL}/api/conversations`, { listing_id }),
  
  getMessages: (conversationId) =>
    axios.get(`${API_URL}/api/conversations/${conversationId}/messages`),
  
  sendMessage: (conversationId, content) =>
    axios.post(`${API_URL}/api/conversations/${conversationId}/messages`, { content }),
  
  getUnreadCount: () =>
    axios.get(`${API_URL}/api/conversations/unread/count`),
  
  markRead: (conversationId) =>
    axios.post(`${API_URL}/api/conversations/${conversationId}/read`)
};

// Support API
export const supportApi = {
  getTickets: () =>
    axios.get(`${API_URL}/api/support/tickets`),
  
  getTicket: (ticketId) =>
    axios.get(`${API_URL}/api/support/tickets/${ticketId}`),
  
  createTicket: (subject, message) =>
    axios.post(`${API_URL}/api/support/tickets`, { subject, message }),
  
  getMessages: (ticketId) =>
    axios.get(`${API_URL}/api/support/tickets/${ticketId}/messages`),
  
  sendMessage: (ticketId, content) =>
    axios.post(`${API_URL}/api/support/tickets/${ticketId}/messages`, { content }),
  
  closeTicket: (ticketId) =>
    axios.put(`${API_URL}/api/support/tickets/${ticketId}/close`),
  
  checkAdmin: () =>
    axios.get(`${API_URL}/api/support/admin/check`),
  
  getUnreadCount: () =>
    axios.get(`${API_URL}/api/support/tickets/unread/count`)
};

// Notifications API
export const notificationsApi = {
  getAll: () =>
    axios.get(`${API_URL}/api/notifications`),
  
  getUnreadCount: () =>
    axios.get(`${API_URL}/api/notifications/unread/count`),
  
  markRead: (notificationId) =>
    axios.put(`${API_URL}/api/notifications/${notificationId}/read`),
  
  markAllRead: () =>
    axios.put(`${API_URL}/api/notifications/read-all`),
  
  sendAdmin: (title, message, target = 'all') =>
    axios.post(`${API_URL}/api/notifications/admin`, { title, message, target })
};

// Favourites API
export const favouritesApi = {
  getAll: () =>
    axios.get(`${API_URL}/api/favourites`),
  
  getIds: () =>
    axios.get(`${API_URL}/api/favourites/ids`),
  
  add: (listingId) =>
    axios.post(`${API_URL}/api/favourites/${listingId}`),
  
  remove: (listingId) =>
    axios.delete(`${API_URL}/api/favourites/${listingId}`)
};

// Geo API
export const geoApi = {
  detect: () =>
    axios.get(`${API_URL}/api/geo/detect`)
};
